import React, { FC, useContext, useEffect, useState } from 'react';
import { getDatePickerFormat } from 'utils/format';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import TimeMinute from 'components/orders/shared/time-minute.component';
import IntExtDeliveryDatePicker from 'components/orders/order-details/int-ext-delivery-date-picker.component';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import moment from 'moment';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { LabClient } from 'models/client-directory';
import { getDeliveryAdvanceWorkingDate, WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import { isValidDeliveryDate } from 'components/order-flow/shared/order-input-helper';

const isSameTime = (a: Date | string | null | undefined, b: Date | string | null | undefined) =>
    (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);

type DeliveryDatesProps = {
    clientSetting: LabClient;
    useOneRowLayout?: boolean;
};
const DeliveryDates: FC<DeliveryDatesProps> = ({ clientSetting, useOneRowLayout }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const currentOrganization = currentOrgVar();
    const [selectedDate, setSelectedDate] = useState<Date>(moment().toDate());
    const [hour, setHour] = useState<number | undefined>(parseInt('00'));
    const [minute, setMinute] = useState<number | undefined>(parseInt('00'));
    const [internalDeliveryDate, setInternalDeliveryDate] = useState<Date | null>(null);
    const [internalDeliveryDaysAdvance, setInternalDeliveryDaysAdvance] = useState<number>();
    const [externalDeliveryDate, setExternalDeliveryDate] = useState<Date | null>(null);
    const [externalDeliveryDaysAdvance, setExternalDeliveryDaysAdvance] = useState<number>();

    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    useEffect(() => {
        const deliveryDate = order.deliveryDetails.date ? new Date(order.deliveryDetails.date) : new Date();
        setSelectedDate(deliveryDate);
        setHour(order.deliveryDetails.time?.hour);
        setMinute(order.deliveryDetails.time?.minute);

        if (order.internalDeliveryDate) {
            setInternalDeliveryDate(new Date(order.internalDeliveryDate));
        }
        if (order.externalDeliveryDate) {
            setExternalDeliveryDate(new Date(order.externalDeliveryDate));
        }

        calculateDefaultDeliveryDate(deliveryDate);
    }, [clientSetting]);

    useEffect(() => {
        const deliveryDateMs = new Date(order.deliveryDetails.date as Date).setHours(0, 0, 0, 0);
        const currentDateMs = new Date().setHours(0, 0, 0, 0);
        if (deliveryDateMs < currentDateMs) {
            updateInternalDeliveryDate(null);
            updateExternalDeliveryDate(null);
            setInternalDeliveryDaysAdvance(0);
            setExternalDeliveryDaysAdvance(0);
        } else {
            calculateDefaultDeliveryDate(
                order.deliveryDetails.date ? moment(order.deliveryDetails.date).toDate() : selectedDate,
            );
        }
    }, [order.deliveryDetails.date]);

    const disableWeekends = (date: Date) => {
        return moment(date).isoWeekday() === 6 || moment(date).isoWeekday() === 7;
    };

    const handleDateChange = (date: Date | null) => {
        // A picker reporting the date it already shows must not write the order again: every
        // write re-renders the pickers, and an echo of the same value looped the step (DD-3648).
        if (isSameTime(date, selectedDate)) {
            return;
        }
        setSelectedDate(date as Date);
        // A date typed halfway in is an Invalid Date (DD-3847): it leaves the order without a
        // date, so the step waits, instead of a value that turns into null on the wire.
        if (!isValidDeliveryDate(date)) {
            setOrder((prev) =>
                prev.deliveryDetails.date === null
                    ? prev
                    : { ...prev, deliveryDetails: { ...prev.deliveryDetails, date: null } },
            );
            return;
        }
        const deliveryDateMs = new Date(date as Date).setHours(0, 0, 0, 0);
        const currentDateMs = new Date().setHours(0, 0, 0, 0);
        if (deliveryDateMs < currentDateMs) {
            updateInternalDeliveryDate(null);
            updateExternalDeliveryDate(null);
            setInternalDeliveryDaysAdvance(0);
            setExternalDeliveryDaysAdvance(0);
        } else {
            calculateDefaultDeliveryDate(date as Date);
        }
        if (date) {
            setOrder((prev) =>
                isSameTime(prev.deliveryDetails.date, date)
                    ? prev
                    : {
                          ...prev,
                          deliveryDetails: {
                              ...prev.deliveryDetails,
                              date: date as Date,
                          },
                      },
            );
        }
    };

    const onTimeChange = (hour: number | undefined, minute: number | undefined) => {
        setHour(hour);
        setMinute(minute);
        setOrder((prev) =>
            prev.deliveryDetails.time?.hour === hour && prev.deliveryDetails.time?.minute === minute
                ? prev
                : {
                      ...prev,
                      deliveryDetails: {
                          ...prev.deliveryDetails,
                          time: {
                              hour: hour,
                              minute: minute,
                          },
                      },
                  },
        );
    };

    const calculateDefaultDeliveryDate = (deliveryDate: Date) => {
        if (clientSetting?.internalDeliveryDaysAdvance) {
            const advanceDate = getDeliveryAdvanceWorkingDate(
                deliveryDate as Date,
                clientSetting.internalDeliveryDaysAdvance as number,
            );
            if (advanceDate) {
                const workingDayCount = WorkingDayBeforeCounter(deliveryDate, advanceDate);
                setInternalDeliveryDaysAdvance(workingDayCount);
                updateInternalDeliveryDate(advanceDate);
            }
        }
        if (clientSetting?.externalDeliveryDaysAdvance) {
            const advanceDate = getDeliveryAdvanceWorkingDate(
                deliveryDate as Date,
                clientSetting.externalDeliveryDaysAdvance as number,
            );
            if (advanceDate) {
                const workingDayCount = WorkingDayBeforeCounter(deliveryDate, advanceDate);
                setExternalDeliveryDaysAdvance(workingDayCount);
                updateExternalDeliveryDate(advanceDate);
            }
        }
    };

    const updateInternalDeliveryDate = (date: Date | null) => {
        setInternalDeliveryDate((prev) => (isSameTime(prev, date) ? prev : date));
        if (date) {
            setOrder((prev) =>
                isSameTime(prev.internalDeliveryDate, date) ? prev : { ...prev, internalDeliveryDate: date },
            );
        }
    };

    const updateExternalDeliveryDate = (date: Date | null) => {
        setExternalDeliveryDate((prev) => (isSameTime(prev, date) ? prev : date));
        if (date) {
            setOrder((prev) =>
                isSameTime(prev.externalDeliveryDate, date) ? prev : { ...prev, externalDeliveryDate: date },
            );
        }
    };

    // The clinic's day as a number, computed from a copy: setHours on selectedDate itself
    // rewrote the picker's own value (and the order's, which shares it) on every render.
    const clinicDeliveryDateMs = selectedDate ? new Date(selectedDate).setHours(0, 0, 0, 0) : undefined;

    const columnCount = isExternalLabUsersEnabled ? 3 : 2;
    const gridSize = useOneRowLayout ? 12 / columnCount : 12;

    return (
        <Paper variant="outlined" sx={{ padding: 2 }}>
            <Grid container spacing={2}>
                <Grid item sm={gridSize}>
                    <Typography variant="h4" mb={1}>
                        {translate('orders:DELIVERY_DATE')}
                    </Typography>
                    {!!order.orderForm?.deliveryDate && (
                        <Box mb={1}>
                            <AiAlert message={translate('order-forms:AI_FOUND_DELIVERY_DATE')} />
                        </Box>
                    )}
                    <Box display="flex">
                        <Box mr={1} flexGrow={1}>
                            <DatePicker
                                format={getDatePickerFormat()}
                                shouldDisableDate={disableWeekends}
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: 'dense',
                                        inputProps: { 'data-test': 'delivery-dates.datepicker' },
                                        error: !isValidDeliveryDate(selectedDate),
                                        helperText: !isValidDeliveryDate(selectedDate)
                                            ? translate('common:VALIDATION.FIELD_REQUIRED')
                                            : undefined,
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <TimeMinute
                                onTimeChange={onTimeChange}
                                hour={hour}
                                minute={minute}
                                showFullHourMinute={true}
                                dataTest="delivery-dates.time-minute"
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item sm={gridSize}>
                    <Typography variant="h4" mb={1} data-test="delivery-dates-internal-title">
                        {translate('orders:INTERNAL_DELIVERY_DATE')}
                    </Typography>
                    <IntExtDeliveryDatePicker
                        selectedDate={internalDeliveryDate}
                        deliveryDaysAdvance={internalDeliveryDaysAdvance}
                        clinicDeliveryDate={clinicDeliveryDateMs}
                        onChangeDate={updateInternalDeliveryDate}
                        dataTest="delivery-dates.internal-delivery-picker"
                    />
                </Grid>
                {isExternalLabUsersEnabled && (
                    <Grid item sm={gridSize}>
                        <Typography variant="h4" mb={1} data-test="delivery-dates-external-title">
                            {translate('orders:EXTERNAL_DELIVERY_DATE')}
                        </Typography>
                        <IntExtDeliveryDatePicker
                            selectedDate={externalDeliveryDate}
                            deliveryDaysAdvance={externalDeliveryDaysAdvance}
                            clinicDeliveryDate={clinicDeliveryDateMs}
                            onChangeDate={updateExternalDeliveryDate}
                            isExternal={true}
                            dataTest="delivery-dates.external-delivery-picker"
                        />
                    </Grid>
                )}
            </Grid>
        </Paper>
    );
};

export default DeliveryDates;
